import { concepts, lessonById, words } from '../data/foundations'
import type { Lesson } from '../types/content'
import type { LearningMode, Progress } from '../types/progress'
import { resolveLessonContent } from './activityModel'
import { getLessonReadiness } from './audioReadiness'
import { canDecodeAt, currentConcept, lessonsFor } from './learningJourney'
import { getReviewQueue } from './reviewScheduler'

export interface PracticeSession { mode: LearningMode; wordId?: string }
export interface PracticePlan { lesson: Lesson; session: PracticeSession; reason: string }

export function resolvePracticeLesson(lesson: Lesson, session?: PracticeSession): Lesson {
  const resolved = resolveLessonContent(lesson)
  if (!session?.wordId || ['hear', 'recognition', 'sound-hunt'].includes(lesson.type)) return resolved
  const word = words.find((item) => item.id === session.wordId)
  const concept = concepts.find((item) => item.id === lesson.conceptId)
  const eligible = word && concept && !word.teacherSupported && word.decodableAfterConcepts.every((id) =>
    (concepts.find((item) => item.id === id)?.order ?? Infinity) <= concept.order)
  if (!eligible) return resolved
  return { ...resolved, wordIds: [word.id] }
}

export function getAvailableProgressLesson(conceptId: string, progress: Progress): Lesson | undefined {
  const list = lessonsFor(conceptId)
  return [...list.filter((item) => !progress.completedLessons.includes(item.id)), ...list.filter((item) => progress.completedLessons.includes(item.id))]
    .map(resolveLessonContent).find((item) => getLessonReadiness(item).available)
}

export function getNextPractice(progress: Progress): PracticePlan | undefined {
  const frontier = currentConcept(progress)
  for (const task of getReviewQueue(progress)) {
    const lesson = lessonById.get(task.lessonId)
    if (!lesson || (concepts.find((item) => item.id === lesson.conceptId)?.order ?? Infinity) > frontier.order) continue
    const session: PracticeSession = { mode: 'review', wordId: task.wordId }
    const resolved = resolvePracticeLesson(lesson, session)
    if (getLessonReadiness(resolved).available) return { lesson: resolved, session, reason: task.reason === 'support' ? '再一起练一小步' : '和老朋友再见一面' }
  }
  const next = getAvailableProgressLesson(frontier.id, progress)
  if (next && !progress.completedLessons.includes(next.id)) return { lesson: next, session: { mode: 'lesson' }, reason: '接着上次的小脚印' }
  const prior = [...concepts].filter((item) => item.order <= frontier.order).reverse()
    .flatMap((item) => lessonsFor(item.id)).filter((item) => progress.completedLessons.includes(item.id))
    .map(resolveLessonContent).find((item) => getLessonReadiness(item).available)
  return prior ? { lesson: prior, session: { mode: 'review' }, reason: '今天练过了，也可以休息' } : undefined
}

/** A new, decodable word checks transfer of encoding; it never claims spoken reading. */
export function getIndependentPractice(lesson: Lesson, progress: Progress): PracticePlan | undefined {
  const concept = concepts.find((item) => item.id === lesson.conceptId)
  if (!concept || concept.stage === 0) return undefined
  const encode = lessonsFor(concept.id).find((item) => item.type === 'encode')
  if (encode) {
    const candidates = words.filter((word) => canDecodeAt(word, concept.order)
      && word.mapping.some((part) => concept.mappingIds.includes(part.mappingId))
      && word.id !== lesson.wordIds[0])
      .sort((a, b) => (progress.learningRecords.words[a.id]?.exposures ?? 0) - (progress.learningRecords.words[b.id]?.exposures ?? 0) || a.difficulty - b.difficulty)
    for (const word of candidates) {
      const session: PracticeSession = { mode: progress.learningRecords.words[word.id]?.exposures ? 'independent' : 'transfer', wordId: word.id }
      const resolved = resolvePracticeLesson(encode, session)
      if (getLessonReadiness(resolved).available) return { lesson: resolved, session, reason: '换一个词，自己试一试' }
    }
  }
  const recognition = lessonsFor(concept.id).find((item) => item.type === 'recognition')
  return recognition && getLessonReadiness(recognition).available
    ? { lesson: recognition, session: { mode: 'independent' }, reason: '这次，自己找一找' } : undefined
}
